import React, { useMemo } from 'react';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, makeStyles, Theme } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import BlockIcon from '@material-ui/icons/Block';
import { green, red } from '@material-ui/core/colors';
import _ from 'lodash';
import { StyledText } from '../StyledText';
import { EntrepreneurProfileBody, Geography, IndustryOption, InvestorMatchOutput } from '../../types/types';
import { getDisplayedLocation, placeMatchingGeographyFirst, placeMatchingIndustriesFirst } from '../../lib';
import { formatDollarsAsMillions } from '../../lib/utils';

const useTableStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: 'auto',
    '& .MuiTableHead-root .MuiTableCell-root': {
      borderColor: theme.palette.primary.main
    }
  }
}));

const useStyles = makeStyles((theme: Theme) => ({
  centerItems: {
    display: 'flex',
    alignItems: 'center'
  }
}));

interface InvestmentCriteriaTableProps {
  entrepreneur: EntrepreneurProfileBody
  investor: InvestorMatchOutput
}

const InvestmentCriteriaTable: React.FC<InvestmentCriteriaTableProps> = ({
  investor, entrepreneur
}) => {
  const tableStyles = useTableStyles();
  const styles = useStyles();

  const isFinanceSoughtMatch = useMemo(() => {
    const soughtHigh = investor.investmentLow <= entrepreneur.financeSoughtHigh;
    const soughLow = investor.investmentHigh >= entrepreneur.financeSoughtLow;
    return soughtHigh && soughLow;
  }, [investor, entrepreneur]);

  const isRevenueMatch = useMemo(() => {
    const isEntRevenueTooLow = entrepreneur.currentRevenue < investor.investmentRevenue;
    return !isEntRevenueTooLow;
  }, [investor, entrepreneur]);


  const { investorIndustryList, entIndustryList, isIndustryMatch, investorGeoList, isGeographyMatch } = useMemo(() => {
    const { investorIndustryList: invList, entIndustryList: entList } = placeMatchingIndustriesFirst(investor.industryList, entrepreneur.industryList);
    let industryMatch = false;
    if (!invList.length) {
      // this shouldn't happen in prod but algorithm will essentially treat lack of value as 'Any', so handle the same way here
      industryMatch = true;
    }
    if (invList.length && (invList[0] === IndustryOption.Any)) {
      industryMatch = true;
    } else if (invList.length && entList.length) {
      industryMatch = invList[0] === entList[0];
    }

    const { list: geoList, match } = placeMatchingGeographyFirst(investor.geographyList, entrepreneur.geography);

    return { investorIndustryList: invList, entIndustryList: entList, isIndustryMatch: industryMatch, investorGeoList: geoList, isGeographyMatch: match };
  }, [investor, entrepreneur]);

  const MatchFlag = ({ b }: { b: boolean }) => (
    b ? <CheckIcon style={{ color: green[500], fontSize: 16, marginRight: 10 }} /> : <BlockIcon style={{ color: red[500], fontSize: 16, marginRight: 10 }} />
  );

  const IndustryCell = ({ list = [] }: { list: string[] }) => (
    <div>
      <StyledText style={{ textTransform: 'capitalize' }}>
        {list.slice(0, 3).join(', ')}
      </StyledText>
      {list.length > 3 && <StyledText variant='subtext2'>{`and ${list.length - 3} other`}</StyledText>}
    </div>
  );

  const GeographyCell = ({ list = [] }: { list: Geography[] }) => (
    <div>
      <StyledText style={{ textTransform: 'capitalize' }}>
        {list.slice(0, 1).map((i) => getDisplayedLocation(i))}
      </StyledText>
      {list.length > 1 && <StyledText variant='subtext2'>{`and ${list.length - 1} other`}</StyledText>}
    </div>
  );

  return (
    <TableContainer classes={tableStyles}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <StyledText color='primary' variant='h5'>
                Criterion
              </StyledText>
            </TableCell>
            <TableCell>
              <StyledText color='primary' variant='h5'>
                Investor
              </StyledText>
            </TableCell>
            <TableCell>
              <StyledText color='primary' variant='h5'>
                Entrepreneur
              </StyledText>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>
              <div className={styles.centerItems}>
                <MatchFlag b={isFinanceSoughtMatch} />
                <StyledText>Investment Amount</StyledText>
              </div>
            </TableCell>
            <TableCell>
              <StyledText>{`${formatDollarsAsMillions(investor.investmentLow)} - ${formatDollarsAsMillions(investor.investmentHigh)} (check size)`}</StyledText>
            </TableCell>
            <TableCell>
              <StyledText>{`${formatDollarsAsMillions(entrepreneur.financeSoughtLow)} - ${formatDollarsAsMillions(entrepreneur.financeSoughtHigh)} (round size)`}</StyledText>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <div className={styles.centerItems}>
                <MatchFlag b={isRevenueMatch} />
                <StyledText>Current Revenue</StyledText>
              </div>
            </TableCell>
            <TableCell>
              <StyledText>{formatDollarsAsMillions(investor.investmentRevenue ? investor.investmentRevenue : 0)}</StyledText>
            </TableCell>
            <TableCell>
              <StyledText>{formatDollarsAsMillions(entrepreneur.currentRevenue ? entrepreneur.currentRevenue : 0)}</StyledText>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <div className={styles.centerItems}>
                <MatchFlag b={isGeographyMatch} />
                <StyledText>Location</StyledText>
              </div>
            </TableCell>
            <TableCell>
              <GeographyCell list={investorGeoList} />
            </TableCell>
            <TableCell>
              <StyledText style={{ textTransform: 'capitalize' }}>{getDisplayedLocation(entrepreneur.geography)}</StyledText>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <div className={styles.centerItems}>
                <MatchFlag b={isIndustryMatch} />
                <StyledText>Industry</StyledText>
              </div>
            </TableCell>
            <TableCell>
              <IndustryCell list={investorIndustryList} />
            </TableCell>
            <TableCell>
              <IndustryCell list={entIndustryList} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <div className={styles.centerItems}>
                <MatchFlag b={entrepreneur.founderFemale || entrepreneur.founderFemale === investor.founderFemale} />
                <StyledText>Female Founder</StyledText>
              </div>
            </TableCell>
            <TableCell><StyledText>{investor.founderFemale ? 'Yes' : 'No'}</StyledText></TableCell>
            <TableCell><StyledText>{entrepreneur.founderFemale ? 'Yes' : 'No'}</StyledText></TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <div className={styles.centerItems}>
                <MatchFlag b={entrepreneur.founderUnderrepresented || entrepreneur.founderUnderrepresented === investor.founderUnderrepresented} />
                <StyledText>Underrepresented Management Team</StyledText>
              </div>
            </TableCell>
            <TableCell><StyledText>{investor.founderUnderrepresented ? 'Yes' : 'No'}</StyledText></TableCell>
            <TableCell><StyledText>{entrepreneur.founderUnderrepresented ? 'Yes' : 'No'}</StyledText></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InvestmentCriteriaTable;
